import { useCase } from "@/features/case/use-case";
import {
  useLazySearchCreditorListDataQuery,
  useLazyGetCreditorListByCreditorIdAndCaseIdQuery,
} from "@/services/creditors/creditorsReducers";
import {
  MotifErrorMessage,
  MotifFormField,
  MotifLabel,
  MotifProgressLoader,
} from "@ey-xd/motif-react";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import CreditorDetails from "./CreditorDetails";
import AddCreditorButton from "./AddCreditorButton";
import ContactForm from "./ContactForm";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";

import { useDispatch, useSelector } from "react-redux";

import { CreditorSearchData, resetCreditorState } from "../creditorSlice";

import useResetAllClaimItemsStore from "../../useResetAllClaimItemsStore";
import { useNavigate } from "react-router-dom";
import NewContactForm from "./NewContactForm";
import SearchInput from "@/components/SearchInput/SearchInput";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
import { useTranslation } from "react-i18next";
import { ActiveTabsOrder } from "@/menuData";

interface RootState {
  claimSetupCreditorSearch: CreditorSearchData;
}

type CreditorSearchProps = {
  setDataInStore: Dispatch<SetStateAction<boolean>>;
  dataInStore: boolean;
  goToNextTab: () => void;
  CreditorDataFromCreditorTab: {
    caseId: string;
    creditorId: string;
  };
  isDescriptionRequired: boolean;
};

function renderCreditorDetails(selectedCreditorDetail: {
  creditorName?: any;
  description?: any;
  creditorReference?: any;
  relationshipToDebtor?: any;
}) {
  if (!selectedCreditorDetail) {
    return null;
  }
  const {
    creditorName,
    description,
    creditorReference,
    relationshipToDebtor: relationShipToDebtor,
  } = selectedCreditorDetail;
  return (
    <>
      <CreditorDetails
        creditorName={creditorName}
        creditorDescription={description}
        creditorReferenceId={creditorReference}
        relationShipToDebtor={relationShipToDebtor}
      />
    </>
  );
}

export default function CreditorSearch({
  dataInStore,
  setDataInStore,
  goToNextTab,
  CreditorDataFromCreditorTab,
  isDescriptionRequired,
}: CreditorSearchProps) {
  const { caseId } = useCase();
  const [searchCreditors, { data: creditorDataByCase, isFetching, isError }] =
    useLazySearchCreditorListDataQuery();

  const selectedCreditorContactDetailsFromStore = useSelector(
    (state: RootState) => {
      return state.claimSetupCreditorSearch.searchCreditorContactDetailsData;
    }
  );

  const selectedCreditorId = useSelector(
    (state: RootState) =>
      state.claimSetupCreditorSearch.selectedCreditorCompleteDetail
  );

  // const newCreditorDetail = useSelector(
  //   (state: RootState) => state.claimSetupCreditorSearch.newCreditorDetail
  // );

  const isNewCreditorData = useSelector(
    (state: RootState) => state.claimSetupCreditorSearch.isNewCreditor
  );
  const [
    getCreditor,
    {
      data: creditorDataByCreditorIdAndCaseID,
      isLoading: isCreditorDataByCreditorIdAndCaseIDLoading,
    },
  ] = useLazyGetCreditorListByCreditorIdAndCaseIdQuery();
  useEffect(() => {
    if (
      caseId &&
      (CreditorDataFromCreditorTab?.creditorId ||
        selectedCreditorContactDetailsFromStore.creditorId)
    ) {
      getCreditor({
        caseId: CreditorDataFromCreditorTab
          ? CreditorDataFromCreditorTab.caseId
          : caseId,
        creditorId: CreditorDataFromCreditorTab?.creditorId
          ? CreditorDataFromCreditorTab.creditorId
          : selectedCreditorContactDetailsFromStore.creditorId,
      });
    }
  }, [
    caseId,
    CreditorDataFromCreditorTab,
    getCreditor,
    selectedCreditorContactDetailsFromStore.creditorId,
  ]);

  const [isVisible, setIsVisible] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [changeCreditor, setChangeCreditor] = useState(false);
  const [selectedCreditorDetail, setSelectedCreditorDetail] = useState<any>({});
  const [isNewCreditor, setIsNewCreditor] = useState(isNewCreditorData);
  const [clearContactForm, setClearContactForm] = useState(false);
  const resetAllClaimItemsStore = useResetAllClaimItemsStore();
  const [searchItems, setSearchItems] = useState<any[]>([]);
  const [isEmpty, setISEmpty] = useState(false);

  useEffect(() => {
    if (selectedCreditorId && creditorDataByCreditorIdAndCaseID?.length > 1) {
      const matchedCreditor = creditorDataByCreditorIdAndCaseID.find(
        (creditor: any) => creditor.creditorId === selectedCreditorId
      );
      if (matchedCreditor) {
        searchCreditors({
          CaseId: caseId,
          SearchText: matchedCreditor.creditorName,
          PageNumber: 1,
          PageSize: 10,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreditorId, creditorDataByCreditorIdAndCaseID]);

  if (isVisible) {
    dispatch(setDiscardEditMode({ editMode: false }));
  }
  const handleCreditorCancel = () => {
    setIsVisible(false);
    dispatch(setDiscardEditMode({ editMode: true }));
  };

  const handleCreditorReset = () => {
    setSelectedCreditorDetail({});
    dispatch(resetCreditorState());
    setIsVisible(false);
    resetAllClaimItemsStore();
    navigate(`/case/${caseId}?activeTab=${ActiveTabsOrder.CLAIM}`);
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    let splitInputValue = inputValue?.split(" | ");
    if (splitInputValue?.length > 1) {
      inputValue = splitInputValue[0];
    }
    if (inputValue === "") {
      setSearchItems([]);
    }
    setSelectedCreditorDetail({});
    setInputValue(inputValue);
    setChangeCreditor(true);
  };
  const handleSearchInputEnter = () => {
    if (inputValue.length < 3) {
      setSearchItems([]);
      return;
    }
    setClearContactForm(false);
    setIsNewCreditor(false);
    setDataInStore(false);
    setISEmpty(false);
    searchCreditors({
      CaseId: caseId,
      SearchText: inputValue,
      PageNumber: 1,
      PageSize: 10,
    })
      .unwrap()
      .then((data: any) => {
        if (data?.results?.length === 0) {
          setSelectedCreditorDetail({});
          setISEmpty(true);
        }
        setSearchItems(
          data?.results.map(
            (creditor: { creditorName: string; creditorReference: string }) =>
              `${creditor?.creditorName} | ${creditor?.creditorReference}`
          )
        );
      })
      .catch((error) => {
        setSearchItems([]);
        setSelectedCreditorDetail({});
        setISEmpty(true);
      });
  };
  const handleSearchInputSelect = (value: string) => {
    const selectedCreditorDetail = creditorDataByCase?.results.find(
      (creditor: { creditorName: string; creditorReference: string }) =>
        `${creditor.creditorName} | ${creditor.creditorReference}` === value
    );
    setInputValue(value);
    setClearContactForm(false);
    setIsNewCreditor(false);
    setDataInStore(false);
    setSelectedCreditorDetail(selectedCreditorDetail);
    setChangeCreditor(true);
  };
  const handleSearchInputClear = () => {
    // setSearchItems([]);
    setClearContactForm(true);
    dispatch(resetCreditorState());
    setClearContactForm(true);
    setChangeCreditor(false);
    setDataInStore(false);
  };
  const showError = false;
  const showErrorMessage =
    "pages.claims.claimCreate.creditorTab.validationMessages.creditorSectionMessage";

  const handleNewCreditorClick = () => {
    setInputValue("");
    setSelectedCreditorDetail({});
    setIsNewCreditor(true);
    dispatch(resetCreditorState());
  };

  const creditorId = selectedCreditorDetail?.creditorId;
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: true }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch]);
  useEffect(() => {
    if (selectedCreditorId && creditorDataByCase) {
      if (!inputValue) {
        const creditorStore = creditorDataByCase?.results.find(
          (creditor: any) =>
            creditor.creditorId ===
              selectedCreditorContactDetailsFromStore.creditorId ||
            creditor.creditorId === selectedCreditorId
        );
        setSelectedCreditorDetail(creditorStore);
        setInputValue(
          `${creditorStore?.creditorName} | ${creditorStore?.creditorReference}`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCreditorId,
    creditorDataByCase,
    selectedCreditorContactDetailsFromStore.creditorId,
  ]);
  useEffect(() => {
    if (
      creditorDataByCreditorIdAndCaseID !== undefined &&
      creditorDataByCreditorIdAndCaseID?.length === 1
    ) {
      setInputValue(
        `${creditorDataByCreditorIdAndCaseID[0]?.creditorName} | ${creditorDataByCreditorIdAndCaseID[0]?.creditorReference}`
      );
      setSelectedCreditorDetail(creditorDataByCreditorIdAndCaseID[0]);
    }
  }, [creditorDataByCreditorIdAndCaseID, caseId, searchCreditors]);

  useEffect(() => {
    if (isNewCreditorData === true) {
      setInputValue("");
    }
  }, [isNewCreditorData]);

  if (isCreditorDataByCreditorIdAndCaseIDLoading) {
    return <MotifProgressLoader />;
  }

  return (
    <div className="">
      <div className="motif-row">
        <div className="motif-col-lg-7">
          <div className="formFieldLabel">
            <MotifLabel>
              {t(
                "pages.claims.claimCreate.creditorTab.formLabelsDetails.searchCreditor"
              )}
              <span className="requiredSymbol">*</span>
            </MotifLabel>
          </div>
          <div >
            <MotifFormField>
              <SearchInput
                placeholder={t(
                  "pages.claims.claimCreate.creditorTab.formLabelsDetails.creditorPlaceHolder"
                )}                
                aria-label="Search for a Creditor"
                open={!isFetching}
                items={searchItems}
                onChange={handleSearchInputChange}
                onSelect={handleSearchInputSelect}
                filterPropFunction={(e: any) => {
                  return e;
                }}
                withOutDebounce={true}
                onEnter={handleSearchInputEnter}
                onClear={handleSearchInputClear}
                value={isNewCreditorData ? "" : inputValue}
                visibleOptions={3}
              />
            </MotifFormField>
            {inputValue?.length < 3 && (
              <MotifLabel>
                {t("global.globalNotifications.searchActivateCharacterLimit", {
                  labelName: "Creditor",
                })}
              </MotifLabel>
            )}
            {isFetching && (
              <MotifLabel>
                {" "}
                {t("global.globalNotifications.loadingMessage")}
              </MotifLabel>
            )}
            {isEmpty && (
              <MotifErrorMessage>
                {t(
                  "pages.claims.claimCreate.creditorTab.validationMessages.noCreditorFound"
                )}
              </MotifErrorMessage>
            )}
            {isError && (
              <MotifErrorMessage>
                {t("global.globalNotifications.wentWrong")}
              </MotifErrorMessage>
            )}
          </div>
        </div>
      </div>
      {showError && (
        <MotifErrorMessage>{t(showErrorMessage)}</MotifErrorMessage>
      )}
      {!isNewCreditor && (
        <AddCreditorButton handleNewCreditorClick={handleNewCreditorClick} />
      )}
      {isNewCreditor && (
        <NewContactForm
          setIsVisible={setIsVisible}
          goToNextTab={goToNextTab}
          isDescriptionRequired={isDescriptionRequired}
        />
      )}
      {!isNewCreditor &&
        selectedCreditorDetail?.creditorId &&
        renderCreditorDetails(selectedCreditorDetail)}
      {!isNewCreditor &&
        selectedCreditorDetail?.creditorId && <ContactForm
        creditorId={creditorId}
        clearContactForm={clearContactForm}
        setIsVisible={setIsVisible}
        goToNextTab={goToNextTab}
        changeCreditor={changeCreditor}
        dataInStore={dataInStore}
        setDataInStore={setDataInStore}
      />}

      <ConfirmationModal
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        cancelBtnApproveHandler={handleCreditorReset}
        cancelBtnRejectHandler={handleCreditorCancel}
      />
    </div>
  );
}
